import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Long enough for the slowest exit (--edl-duration-check), so a leaving item
// is gone once it has faded. Reduced motion ends the fade at once; the item
// is invisible meanwhile.
const LEAVE_MS = 400;

/**
 * Items in one grid cell: the current one, plus any still fading out. A new
 * value enters (data-enter) and the old one leaves (data-leaving); the first
 * value shows without animating. `same` decides whether a value changed.
 */
function useSwapItems(value, same) {
  const nextId = useRef(1);
  const [items, setItems] = useState(() => (value == null ? [] : [{ id: 0, value, enter: false, leaving: false }]));
  useLayoutEffect(() => {
    setItems((prev) => {
      const current = prev.find((item) => !item.leaving);
      if (current ? same(current.value, value) : value == null) return prev;
      const leaving = prev.map((item) => (item.leaving ? item : { ...item, leaving: true }));
      return value == null ? leaving : [...leaving, { id: nextId.current++, value, enter: true, leaving: false }];
    });
  }, [value]);
  const timers = useRef(new Map());
  useEffect(() => {
    for (const item of items) {
      if (!item.leaving || timers.current.has(item.id)) continue;
      timers.current.set(item.id, window.setTimeout(() => {
        timers.current.delete(item.id);
        setItems((prev) => prev.filter((other) => other.id !== item.id));
      }, LEAVE_MS));
    }
  }, [items]);
  useEffect(() => () => {
    for (const timer of timers.current.values()) window.clearTimeout(timer);
  }, []);
  return items;
}

const sameRef = (a, b) => a === b;
const sameText = (a, b) => String(a) === String(b);

/**
 * An icon that swaps in place (transitions.dev Icon swap): the old one
 * shrinks, blurs and fades while the new one does the reverse. Button uses
 * it for every icon, so changing `leadingIcon` is enough.
 */
export function SwapIcon({ icon, size, strokeWidth = 1.5 }) {
  const items = useSwapItems(icon ?? null, sameRef);
  if (!items.length) return null;
  return (
    <span className="edl-swap edl-swap--icon" aria-hidden="true">
      {items.map(({ id, value: Icon, enter, leaving }) => (
        <span key={id} className="edl-swap__item" data-enter={enter ? '' : undefined} data-leaving={leaving ? '' : undefined}>
          <Icon size={size} strokeWidth={strokeWidth} aria-hidden="true" />
        </span>
      ))}
    </span>
  );
}

// Longer than --edl-duration, after which the box returns to its own width.
const RESIZE_MS = 260;

/**
 * Text that swaps in place (transitions.dev Text states swap): the old text
 * leaves upward, the new one rises in, and the box eases from the old text's
 * width to the new one's, so whatever sits beside it slides rather than
 * jumps. Only the current text is in the accessibility tree. Children may be
 * any node; pass `value` (a string) to say when they count as changed.
 */
export function SwapText({ children, value, className }) {
  const key = value ?? (children == null ? '' : String(children));
  const items = useSwapItems(key, sameText);
  // The leaving item keeps what it showed; the current one renders live.
  const shown = useRef(new Map());
  const current = items.find((item) => !item.leaving);
  // Before the layout effect catches up, the current item still holds the
  // old value; only remember children that belong to it.
  if (current && current.value === key) shown.current.set(current.id, children);
  for (const id of shown.current.keys()) if (!items.some((item) => item.id === id)) shown.current.delete(id);

  // The leaving text is out of the layout (absolute), so the box already has
  // the new width here. Start it at the old width and let the CSS ease it.
  const box = useRef(null);
  const lastWidth = useRef(null);
  const resizeTimer = useRef(undefined);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const from = el.dataset.resizing != null ? el.getBoundingClientRect().width : lastWidth.current;
    el.style.width = '';
    const to = el.getBoundingClientRect().width;
    lastWidth.current = to;
    if (from == null || Math.abs(from - to) < 0.5) return;
    window.clearTimeout(resizeTimer.current);
    delete el.dataset.resizing;
    el.style.width = `${from}px`;
    void el.offsetWidth;
    el.dataset.resizing = '';
    el.style.width = `${to}px`;
    resizeTimer.current = window.setTimeout(() => {
      delete el.dataset.resizing;
      el.style.width = '';
    }, RESIZE_MS);
  }, [items]);
  useEffect(() => () => window.clearTimeout(resizeTimer.current), []);

  return (
    <span ref={box} className={['edl-swap', 'edl-swap--text', className].filter(Boolean).join(' ')}>
      {items.map(({ id, value: itemKey, enter, leaving }) => (
        <span
          key={id}
          className="edl-swap__item"
          data-enter={enter ? '' : undefined}
          data-leaving={leaving ? '' : undefined}
          aria-hidden={leaving ? true : undefined}
        >
          {leaving || itemKey !== key ? shown.current.get(id) : children}
        </span>
      ))}
    </span>
  );
}
